// 瀏覽器 WebAuthn ↔ JSON（伺服器的 options 與回應都是 base64url 字串）。
// 新瀏覽器有 PublicKeyCredential.parse*OptionsFromJSON / credential.toJSON()，舊的自己轉。

type Json = Record<string, unknown>;

export function passkeySupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function' && !!navigator.credentials;
}

export function b64urlToBuf(s: string): ArrayBuffer {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const bin = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

export function bufToB64url(b: ArrayBuffer | ArrayBufferView): string {
  const bytes = b instanceof ArrayBuffer ? new Uint8Array(b) : new Uint8Array(b.buffer, b.byteOffset, b.byteLength);
  let s = '';
  for (const x of bytes) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

type PKC = typeof PublicKeyCredential & {
  parseCreationOptionsFromJSON?: (o: unknown) => PublicKeyCredentialCreationOptions;
  parseRequestOptionsFromJSON?: (o: unknown) => PublicKeyCredentialRequestOptions;
};

function unwrap(o: Json): Json {
  return (o.publicKey as Json) ?? o;
}

export function toCreationOptions(json: Json): PublicKeyCredentialCreationOptions {
  const o = unwrap(json);
  const P = window.PublicKeyCredential as PKC;
  if (P.parseCreationOptionsFromJSON) return P.parseCreationOptionsFromJSON(o);
  const user = o.user as Json;
  return {
    ...(o as unknown as PublicKeyCredentialCreationOptions),
    challenge: b64urlToBuf(o.challenge as string),
    user: { ...(user as unknown as PublicKeyCredentialUserEntity), id: b64urlToBuf(user.id as string) },
    excludeCredentials: ((o.excludeCredentials as Json[]) ?? []).map((c) => ({ ...(c as unknown as PublicKeyCredentialDescriptor), id: b64urlToBuf(c.id as string) })),
  };
}

export function toRequestOptions(json: Json): PublicKeyCredentialRequestOptions {
  const o = unwrap(json);
  const P = window.PublicKeyCredential as PKC;
  if (P.parseRequestOptionsFromJSON) return P.parseRequestOptionsFromJSON(o);
  return {
    ...(o as unknown as PublicKeyCredentialRequestOptions),
    challenge: b64urlToBuf(o.challenge as string),
    allowCredentials: ((o.allowCredentials as Json[]) ?? []).map((c) => ({ ...(c as unknown as PublicKeyCredentialDescriptor), id: b64urlToBuf(c.id as string) })),
  };
}

export function credentialToJSON(cred: PublicKeyCredential): Json {
  const c = cred as PublicKeyCredential & { toJSON?: () => Json };
  if (typeof c.toJSON === 'function') return c.toJSON();
  const r = cred.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse;
  const response: Json = { clientDataJSON: bufToB64url(r.clientDataJSON) };
  if ('attestationObject' in r && r.attestationObject) {
    response.attestationObject = bufToB64url(r.attestationObject);
    response.transports = typeof r.getTransports === 'function' ? r.getTransports() : [];
  } else {
    response.authenticatorData = bufToB64url(r.authenticatorData);
    response.signature = bufToB64url(r.signature);
    if (r.userHandle) response.userHandle = bufToB64url(r.userHandle);
  }
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response,
    clientExtensionResults: cred.getClientExtensionResults(),
    authenticatorAttachment: (cred as PublicKeyCredential & { authenticatorAttachment?: string }).authenticatorAttachment ?? null,
  };
}
